import { z } from 'zod'

import { GetBillCategorySchema } from './bill-category'

export const XeroInvoiceStatusEnum = z.enum([
  'PENDING',
  'DRAFT',
  'SUBMITTED',
  'AUTHORISED',
  'PAID',
  'VOIDED',
  'DELETED',
  'FAILED',
] as const)

/** Statuses Xero gives a credit note; Platform has no pending or failed one of its own. */
export const XeroCreditNoteStatusEnum = z.enum([
  'DRAFT',
  'SUBMITTED',
  'AUTHORISED',
  'PAID',
  'VOIDED',
  'DELETED',
] as const)
export type XeroCreditNoteStatus = z.infer<typeof XeroCreditNoteStatusEnum>

export const XeroQuoteStatusEnum = z.enum([
  'PENDING',
  'DRAFT',
  'SENT',
  'ACCEPTED',
  'DECLINED',
  'INVOICED',
  'DELETED',
  'FAILED',
] as const)

export const XeroTaxTypeEnum = z.enum([
  'OUTPUT2',
  'ZERORATEDOUTPUT',
  'EXEMPTOUTPUT',
  'ECZROUTPUT',
  'ECZROUTPUTSERVICES',
  'REVERSECHARGES',
  'INPUT2',
  'ZERORATEDINPUT',
  'NONE',
] as const)

export const GetXeroItemSchema = z.object({
  code: z.string().nullish(),
  description: z.string().nullish(),
  name: z.string().nullish(),
  sales_account_code: z.string().nullish(),
  sales_unit_price: z.number().nullish(),
  tax_type: z.string().nullish(),
})

/** Payment state of a load's Xero documents of one type, rolled up by the loads index. */
export const XeroDocumentsPaymentStatusEnum = z.enum([
  'none',
  'pending',
  'unpaid',
  'partially_paid',
  'paid',
] as const)
export type XeroDocumentsPaymentStatus = z.infer<typeof XeroDocumentsPaymentStatusEnum>

/** One Xero document as listed on a loads-table row (display only). */
export const GetLoadXeroDocumentSchema = z.object({
  amount: z.number().nullable(),
  /** In the Load's Base currency; null when Xero sent no base amount. */
  amount_base: z.number().nullish(),
  amount_decimal: z.number().nullable(),
  amount_due: z.number().nullable(),
  amount_due_decimal: z.number().nullable(),
  /** Null for invoices and uncategorised bills. */
  category: GetBillCategorySchema.nullish(),
  currency: z.string().nullable(),
  due_date: z.string().nullable(),
  fully_paid_on_date: z.string().nullable(),
  id: z.number(),
  /** Loads sharing this Xero document; amounts are document-level when > 1. */
  load_count: z.number().default(1),
  reference: z.string().nullish(),
  status: XeroInvoiceStatusEnum.nullable(),
  type: z.enum(['invoice', 'bill']),
  xero_deep_link: z.string().nullable(),
  xero_invoice_id: z.string().nullable(),
  xero_invoice_number: z.string().nullable(),
})
export type LoadXeroDocument = z.infer<typeof GetLoadXeroDocumentSchema>

/** One customer proforma (Xero quote) as listed on a loads-table row; `number` is null for a Draft. */
export const GetLoadXeroProformaSchema = z.object({
  /** Total in cents. Missing on a row indexed before amounts were listed. */
  amount: z.number().nullish(),
  /** In the Load's Base currency; null when Xero sent no base amount. */
  amount_base: z.number().nullish(),
  currency: z.string().nullish(),
  /** Quote expiry, `Y-m-d`, when the loads list includes it. */
  expiry_date: z.string().nullish(),
  id: z.number(),
  number: z.string().nullable(),
  status: XeroQuoteStatusEnum.nullable(),
  xero_deep_link: z.string().nullable(),
})
export type LoadXeroProforma = z.infer<typeof GetLoadXeroProformaSchema>

/**
 * One Xero sales credit note as listed on a loads-table row: connected to the Load, or only
 * allocated in Xero to one of its invoices. `amount` is positive, in the credit note's currency.
 */
export const GetLoadXeroCreditNoteSchema = z.object({
  amount: z.number(),
  amount_base: z.number().nullish(),
  amount_decimal: z.number(),
  /** Connected to this Load, in which case it reduces the Load's revenue. */
  connected: z.boolean(),
  /** Every Load the credit note is connected to; may include this one. */
  connected_load_ids: z.number().array().default([]),
  currency: z.string().nullable(),
  /** Unique within the Load, for example `connected-12` or `allocation-2046-<uuid>`. */
  id: z.string(),
  /** The Load's invoice the credit note is allocated to in Xero, if any. */
  invoice_number: z.string().nullable(),
  number: z.string().nullable(),
  /** Counted in this Load's revenue, netted off a Platform-created invoice, or in no Load's revenue. */
  revenue_status: z.enum(['assigned', 'netted', 'unassigned']),
  /** Xero status: DRAFT, SUBMITTED, AUTHORISED, PAID, VOIDED or DELETED. */
  status: z.string().nullable(),
  xero_credit_note_id: z.string(),
  xero_deep_link: z.string().nullable(),
})
export type LoadXeroCreditNote = z.infer<typeof GetLoadXeroCreditNoteSchema>

/** A Xero credit note applied to an invoice; created in Xero, display only. Amounts are in the document currency. */
export const XeroInvoiceCreditNoteSchema = z.object({
  applied_amount: z.number(),
  applied_amount_decimal: z.number(),
  /** Loads the credit note is connected to; null when the API did not load them. */
  connected_load_ids: z.array(z.number()).nullish().default(null),
  date: z.string().nullable(),
  number: z.string().nullable(),
  /**
   * How it counts in load revenue: netted off a Platform-created invoice automatically, assigned
   * to the loads in connected_load_ids, or unassigned (in no load's revenue). Null when not loaded.
   */
  revenue_status: z.enum(['netted', 'assigned', 'unassigned']).nullish().default(null),
  /** Xero status: AUTHORISED (credit remaining) or PAID (fully allocated); null until looked up. */
  status: z.string().nullish().default(null),
  xero_credit_note_id: z.string(),
  xero_deep_link: z.string().nullable(),
})
export type XeroInvoiceCreditNote = z.infer<typeof XeroInvoiceCreditNoteSchema>

/** A Xero position attached to a connected invoice; editing preselects it by its LineItemID. */
const GetXeroInvoiceItemSchema = z.object({
  description: z.string().nullish(),
  xero_line_item_id: z.string().nullable(),
})

export const GetXeroInvoiceSchema = z.object({
  amount: z.number().nullable(),
  amount_base: z.number().nullish(),
  amount_decimal: z.number().nullable(),
  amount_due: z.number().nullish(),
  amount_due_decimal: z.number().nullish(),
  amount_paid: z.number().nullish(),
  amount_paid_decimal: z.number().nullish(),
  base_currency: z.string().nullish(),
  company: z.object({ id: z.number(), title: z.string().nullish() }).nullish(),
  connected_line_count: z.number().nullish().default(null),
  created_at: z.coerce.date(),
  /** Credit notes applied to the whole Xero document. */
  credit_notes: z.array(XeroInvoiceCreditNoteSchema).nullish().default([]),
  /** Null until the first sync after credit notes shipped. */
  credit_notes_amount: z.number().nullish(),
  credit_notes_amount_decimal: z.number().nullish(),
  currency: z.string().nullable(),
  customer_reference: z.string().nullable(),
  /** Whole Xero document, incl. VAT, in cents; null when unknown. */
  document_line_count: z.number().nullish().default(null),
  document_total: z.number().nullish().default(null),
  document_total_decimal: z.number().nullish().default(null),
  due_date: z.string().nullable(),
  error_message: z.string().nullable(),
  exchange_date: z.string().nullish(),
  exchange_rate: z.string().nullish(),
  fully_paid_on_date: z.string().nullish(),
  id: z.number(),
  /** True when this load holds fewer lines than the Xero document (a consolidated invoice). */
  is_consolidated: z.boolean().nullish().default(false),
  /** False for a whole-document invoice, whose positions cannot be changed; null when unknown. */
  is_line_scoped: z.boolean().nullish().default(null),
  /** The positions this load holds; null when the response leaves them out. */
  items: z.array(GetXeroInvoiceItemSchema).nullish().default(null),
  load_id: z.number(),
  matches_load_customer: z.boolean().nullish(),
  notes: z.string().nullish(),
  reference: z.string().nullable(),
  status: XeroInvoiceStatusEnum.nullable(),
  tax_amount: z.number().nullable(),
  tax_amount_decimal: z.number().nullable(),
  tax_type: XeroTaxTypeEnum.nullable(),
  updated_at: z.coerce.date(),
  xero_contact_id: z.string().nullable(),
  xero_deep_link: z.string().nullable(),
  xero_invoice_id: z.string().nullable(),
  xero_invoice_number: z.string().nullable(),
})
export type GetXeroInvoice = z.infer<typeof GetXeroInvoiceSchema>

export const CreateXeroInvoiceSchema = z.object({
  confirm_missing_vat: z.boolean().optional(),
  date: z.string().nullish(),
  description: z.string().max(4000).nullish(),
  due_date: z.string().nullish(),
  item_code: z.string().max(30).nullish(),
  reference: z.string().max(255).nullish(),
})

export const ConnectXeroInvoiceSchema = z.object({
  // Omitted or empty attaches every position of the invoice.
  line_item_ids: z.array(z.string()).nullish(),
  notes: z.string().max(4000).nullish(),
  url: z.string(),
})

export const UpdateXeroInvoiceSchema = z.object({
  // Omitted keeps the attached positions.
  line_item_ids: z.array(z.string()).min(1).optional(),
  notes: z.string().max(4000).nullable(),
})

export const GetXeroInvoicePreviewItemSchema = z.object({
  already_used: z.boolean(),
  connected_load_id: z.number().nullable(),
  currency: z.string().nullable(),
  description: z.string(),
  line_amount: z.number(),
  line_amount_decimal: z.number(),
  quantity: z.number().nullish(),
  xero_line_item_id: z.string().nullable(),
})

export const GetXeroInvoicePreviewSchema = z.object({
  already_connected: z.boolean(),
  base_currency: z.string().nullable(),
  connected_load_id: z.number().nullable(),
  connected_load_ids: z.number().array(),
  contact_name: z.string().nullable(),
  currency: z.string().nullable(),
  due_date: z.string().nullable(),
  fully_connected: z.boolean(),
  items: GetXeroInvoicePreviewItemSchema.array(),
  reference: z.string().nullable(),
  status: XeroInvoiceStatusEnum.nullable(),
  total: z.number(),
  total_base: z.number().nullable(),
  total_base_decimal: z.number().nullable(),
  total_decimal: z.number(),
  total_tax: z.number(),
  total_tax_decimal: z.number(),
  xero_contact_id: z.string().nullable(),
  xero_invoice_id: z.string().nullable(),
  xero_invoice_number: z.string().nullable(),
})

export type GetXeroInvoicePreview = z.infer<typeof GetXeroInvoicePreviewSchema>
export type GetXeroInvoicePreviewItem = z.infer<typeof GetXeroInvoicePreviewItemSchema>

/** A Xero sales credit note allocated to an invoice; amounts are in cents of the credit note currency. */
export const XeroCreditNoteAllocationSchema = z.object({
  amount: z.number(),
  amount_decimal: z.number().nullish(),
  date: z.string().nullish(),
  xero_invoice_id: z.string(),
  xero_invoice_number: z.string().nullish(),
})
export type XeroCreditNoteAllocation = z.infer<typeof XeroCreditNoteAllocationSchema>

/** A Xero sales credit note connected to a load; it reduces the load's revenue. */
export const GetXeroCreditNoteSchema = z.object({
  allocations: z.array(XeroCreditNoteAllocationSchema).nullish().default([]),
  /** Gross amount of the connected lines. */
  amount: z.number().nullable(),
  amount_base: z.number().nullish(),
  amount_base_decimal: z.number().nullish(),
  amount_decimal: z.number().nullish(),
  base_currency: z.string().nullish(),
  contact_name: z.string().nullish(),
  created_at: z.coerce.date(),
  currency: z.string().nullable(),
  date: z.string().nullish(),
  exchange_date: z.string().nullish(),
  exchange_rate: z.string().nullish(),
  id: z.number(),
  items: z.array(z.unknown()).nullish(),
  load_id: z.number(),
  notes: z.string().nullish(),
  reference: z.string().nullish(),
  remaining_credit: z.number().nullish(),
  remaining_credit_decimal: z.number().nullish(),
  /** Xero status, for example DRAFT, AUTHORISED, PAID or VOIDED. */
  status: z.string().nullish(),
  tax_amount: z.number().nullish(),
  tax_amount_decimal: z.number().nullish(),
  updated_at: z.coerce.date(),
  user_id: z.number().nullish(),
  xero_contact_id: z.string().nullish(),
  xero_credit_note_id: z.string(),
  xero_credit_note_number: z.string().nullable(),
  xero_deep_link: z.string().nullish(),
})
export type GetXeroCreditNote = z.infer<typeof GetXeroCreditNoteSchema>

export const ConnectXeroCreditNoteSchema = z.object({
  // Omitted or empty attaches every line of the credit note.
  line_item_ids: z.array(z.string()).nullish(),
  notes: z.string().max(4000).nullish(),
  url: z.string(),
})

export const GetXeroCreditNotePreviewSchema = z.object({
  already_connected: z.boolean(),
  base_currency: z.string().nullish(),
  connected_load_id: z.number().nullish(),
  connected_load_ids: z.number().array().nullish().default([]),
  contact_name: z.string().nullish(),
  currency: z.string().nullish(),
  date: z.string().nullish(),
  fully_connected: z.boolean().nullish(),
  items: GetXeroInvoicePreviewItemSchema.array(),
  reference: z.string().nullish(),
  remaining_credit: z.number().nullish(),
  remaining_credit_decimal: z.number().nullish(),
  status: z.string().nullish(),
  total: z.number(),
  total_base: z.number().nullish(),
  total_base_decimal: z.number().nullish(),
  total_decimal: z.number().nullish(),
  total_tax: z.number().nullish(),
  total_tax_decimal: z.number().nullish(),
  xero_contact_id: z.string().nullish(),
  xero_credit_note_id: z.string().nullish(),
  xero_credit_note_number: z.string().nullish(),
})

export type GetXeroCreditNotePreview = z.infer<typeof GetXeroCreditNotePreviewSchema>

export const CreateXeroQuoteSchema = z.object({
  confirm_missing_vat: z.boolean().optional(),
  date: z.string().nullish(),
  description: z.string().max(4000).nullish(),
  expiry_date: z.string().nullish(),
  item_code: z.string().max(30).nullish(),
  reference: z.string().max(255).nullish(),
})

export const GetXeroQuoteSchema = z.object({
  amount: z.number().nullable(),
  amount_base: z.number().nullish(),
  amount_decimal: z.number().nullable(),
  base_currency: z.string().nullish(),
  created_at: z.coerce.date(),
  currency: z.string().nullable(),
  error_message: z.string().nullable(),
  exchange_date: z.string().nullish(),
  exchange_rate: z.string().nullish(),
  expiry_date: z.string().nullish(),
  id: z.number(),
  load_id: z.number(),
  matches_load_customer: z.boolean().nullish(),
  notes: z.string().nullish(),
  reference: z.string().nullable(),
  status: XeroQuoteStatusEnum.nullable(),
  tax_amount: z.number().nullable(),
  tax_amount_decimal: z.number().nullable(),
  tax_type: XeroTaxTypeEnum.nullable(),
  updated_at: z.coerce.date(),
  xero_contact_id: z.string().nullable(),
  xero_deep_link: z.string().nullable(),
  xero_quote_id: z.string().nullable(),
  xero_quote_number: z.string().nullable(),
})
export type GetXeroQuote = z.infer<typeof GetXeroQuoteSchema>

export const ConnectXeroQuoteSchema = z.object({
  notes: z.string().max(4000).nullish(),
  url: z.string(),
})

export const UpdateXeroQuoteSchema = z.object({
  notes: z.string().max(4000).nullable(),
})

/** Display only: a proforma is always connected as a whole document. */
export const GetXeroQuotePreviewItemSchema = z.object({
  currency: z.string().nullable(),
  description: z.string(),
  line_amount: z.number(),
  line_amount_decimal: z.number(),
  quantity: z.number().nullish(),
  xero_line_item_id: z.string().nullable(),
})

export const GetXeroQuotePreviewSchema = z.object({
  /** Advisory: the same proforma may be connected to several loads. */
  already_connected: z.boolean(),
  base_currency: z.string().nullable(),
  connected_load_id: z.number().nullable(),
  connected_load_ids: z.number().array(),
  contact_name: z.string().nullable(),
  currency: z.string().nullable(),
  expiry_date: z.string().nullable(),
  items: GetXeroQuotePreviewItemSchema.array(),
  reference: z.string().nullable(),
  status: XeroQuoteStatusEnum.nullable(),
  total: z.number(),
  total_base: z.number().nullable(),
  total_base_decimal: z.number().nullable(),
  total_decimal: z.number(),
  total_tax: z.number(),
  total_tax_decimal: z.number(),
  xero_contact_id: z.string().nullable(),
  xero_quote_id: z.string().nullable(),
  xero_quote_number: z.string().nullable(),
})

export type GetXeroQuotePreview = z.infer<typeof GetXeroQuotePreviewSchema>
export type GetXeroQuotePreviewItem = z.infer<typeof GetXeroQuotePreviewItemSchema>

const XeroFormDefaultsRateSchema = z.object({
  amount: z.number(),
  currency: z.string(),
})

const XeroFormDefaultsTaxSchema = z.object({
  amount: z.number(),
  rate_percent: z.number(),
  tax_type: z.string().nullish(),
})

const XeroFormDefaultsTotalSchema = z.object({
  amount: z.number(),
})

const XeroFormDefaultsSuggestedItemSchema = z.object({
  item: GetXeroItemSchema.nullable(),
  item_code: z.string().nullish(),
})

const xeroFormDefaultsBase = {
  customer_has_vat: z.boolean().optional(),
  date: z.string(),
  description: z.string(),
  rate: XeroFormDefaultsRateSchema.nullable(),
  reference: z.string(),
  suggested_item: XeroFormDefaultsSuggestedItemSchema.nullable(),
  tax: XeroFormDefaultsTaxSchema.nullable(),
  total: XeroFormDefaultsTotalSchema.nullable(),
}

export const GetXeroQuoteFormDefaultsSchema = z.object({
  ...xeroFormDefaultsBase,
  expiry_date: z.string().nullable(),
})

export const GetXeroInvoiceFormDefaultsSchema = z.object({
  ...xeroFormDefaultsBase,
  due_date: z.string().nullable(),
})
